(ns frontend.view.entity-merge.kyc
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.util :as cu]
   [common.localization :as loc]
   [frontend.view.entity-merge.components :as components]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [common.schema.entities.kyc.options :as kyc-options]
   [common.schema.entities.workspace.kyc.kyc-process :as kyc-process-schema]
   [common.schema.registry :as registry]
   [common.languages :as languages]
   [reagent.core :as r]))

(defonce selection-path [:selection :kyc-profiles])

(defmethod components/step-info :kyc-profiles
  [_signals [_step-kw step-params]]
  (loc/tr :entity-merge-step/kyc-profiles-info
      [(->> (loc/tr step-params)
            (string/lower-case))]))

(defn kyc-process-view
  [_signals {:kyc-process/keys [status template language invitee-email invitee-phone]
             :common/keys [created-on updated-on]
             :as _kyc-process}]
  [:tr
   {:class-name (fu/cs (pr-str status))}
   [:td.zap-table__cell (kyc-options/process-status-label status)]
   [:td.zap-table__cell
    (kyc-process-schema/template-label template)]
   [:td.zap-table__cell (languages/get-label language)]
   [:td.zap-table__cell invitee-email]
   [:td.zap-table__cell invitee-phone]
   [:td.zap-table__cell (fu/format-date-ts created-on)]
   [:td.zap-table__cell (fu/format-date-ts updated-on)]])

(defn kyc-process-entities
  [r-lookup kyc-process-ids]
  (let [lookup @r-lookup]
    (->> kyc-process-ids
       (keep (fn [id]
               (get lookup id)))
       (sort-by :common/created-on)
       reverse
       doall)))

(defn kyc-processes-view
  [{:keys [r-kyc-processes-by-id] :as signals} kyc-process-ids]
  (let [;;lookup @r-kyc-processes-by-id
        kyc-processes @(r/track kyc-process-entities
                                r-kyc-processes-by-id
                                kyc-process-ids)]
    (if (empty? kyc-processes)
      [:div.row.pl-3.py-3
       (loc/tr :no-kyc-processes-for-profile)]
      [:div.py-3
       [:div (loc/tr :kyc-processes)]
       [:div
        [:table.zap-table.zap-table--striped
         {:style {:width "100%"}}
         [:thead.zap-table__head
          [:tr
           [:th.zap-table__headline (loc/tr :kyc-process/status)]
           [:th.zap-table__headline (loc/tr :kyc-process/template)]
           [:th.zap-table__headline (loc/tr :kyc-process/language)]
           [:th.zap-table__headline (loc/tr :kyc-process/invitee-email)]
           [:th.zap-table__headline (loc/tr :kyc-process/invitee-phone)]
           [:th.zap-table__headline (loc/tr :common/created-on)]
           [:th.zap-table__headline (loc/tr :common/updated-on)]]]
         [:tbody.zap-table__body
          (doall
           (for [{:keys [db/id] :as process} kyc-processes]
             ^{:key (str "kyc-process-" id)}
             [kyc-process-view signals process]))]]]])))

(defn kyc-profile-view
  [signals {:kyc-profile/keys [kyc-processes] :as kyc-profile} show-details?]
  [:div.kyc-profile-info
   [:div.kyc-profile-name
    (entity/display-string kyc-profile :long @session/entities)]
   [:div.kyc-profile-basic-info
    (doall
     (for [k [:kyc-profile/status :kyc-profile/kyc-country
              :kyc-profile/risk-category :kyc-profile/their-kyc-contact]

           :let [fs (registry/get-field-schema @session/tenant-schema k)
                 v (fu/display-data @session/tenant-schema
                                    @session/tenant
                                    k fs kyc-profile)]
           :when (and (some? (get kyc-profile k))
                      (some? v))]
       ^{:key (str k)}
       [:span.mr-2 [:span.text-bold (loc/tr k)] ": " v]))]
   (when show-details?
     [:div.kyc-profile-details
      [kyc-processes-view signals kyc-processes]])])

(defn profiles-list
  [signals _]
  (let [local-state (r/atom {})
        details?
        (fn [k]
          (not (get-in @local-state [k :show-details?])))
        toggle-show-details!
        (fn [k]
          (swap! local-state update-in [k :show-details?] not))]
    (fn [_ {:keys [selection kyc-profiles select!]}]
      [:<>
       (doall
        (for [{kyc-profile-id :db/id
               :as kyc-profile} kyc-profiles
              :let [selected? (= selection kyc-profile-id)
                    show-details? (details? kyc-profile-id)]]
          ^{:key (str "kyc-profile-selection-" kyc-profile-id)}
          [:div
           [:div
            {:class-name (fu/cs "asset-updater-table__current-value mb-2"
                                (when selected?
                                  "selected"))
             :on-click (r/partial select! kyc-profile-id)}
            (if selected?
              [:i.fas.fa-check-circle.mr-2]
              [:i.fal.fa-circle.mr-2])
            [kyc-profile-view signals kyc-profile show-details?]]
           [:div.text-color--info.title-h5.pointer.mb-4
            {:on-click (r/partial toggle-show-details! kyc-profile-id)}
            (if show-details?
              (loc/tr :show-less)
              (loc/tr :show-details))]]))])))

(defn preselect!
  [{:keys [state r-target-kyc-profiles]}]
  (let [selection (get-in @state selection-path)
        no-selection? (nil? selection)
        to-select (->> @r-target-kyc-profiles
                       (mapv :db/id)
                       first)]
    (when no-selection?
      (swap! state assoc-in selection-path to-select))))

(defn select!
  [state path id]
  (swap! state update-in path
         (fn [old-val]
           (if (= id old-val)
             nil
             id))))

(defn profiles-view
  [{:keys [state r-target-kyc-profiles r-source-kyc-profiles] :as signals}]
  (preselect! signals)
  (fn [_]
    (let [source-profiles @r-source-kyc-profiles
          target-profiles @r-target-kyc-profiles
          selection (get-in @state selection-path)]
      [:div.asset-updater-table__fields
       [:div.asset-updater-table__field
        [:div.asset-updater-table__compare
         [:div.row.mt-4
          [:div.col-6
           [profiles-list
            signals
            {:kyc-profiles source-profiles
             :selection selection
             :select! (r/partial select! state selection-path)}]]
          [:div.col-6
           [profiles-list
            signals
            {:kyc-profiles target-profiles
             :selection selection
             :select! (r/partial select! state selection-path)}]]]]]])))

(defn kyc-profile-info
  [signals kyc-profile]
  [kyc-profile-view signals kyc-profile true])

(defn info
  [{:keys [state r-target-kyc-profiles r-source-kyc-profiles] :as signals}]
  (let [source-profiles @r-source-kyc-profiles
        target-profiles @r-target-kyc-profiles
        selection (get-in @state selection-path)
        kyc-profile (->> (concat target-profiles source-profiles)
                         (cu/filter-by :db/id selection)
                         first)]
    [:div
     (if kyc-profile
       [:div (loc/tr :entity-merge-step/review-kyc-profiles-info)]
       [:div (loc/tr :entity-merge-step/review-no-kyc-profiles-info)])
     [kyc-profile-info signals kyc-profile]]))

(defn review
  [signals]
  (let [title (loc/tr :kyc-profiles)]
    [components/review-card signals
     {:title title
      :collapsed-path [:kyc-profiles]
      :content
      [info signals]}]))
